import { useTranslation } from "@next-platform/client-common-core";
import type { TFunction } from "i18next";
import { useCallback, useMemo } from "react";

import type { MetaProperties, PageId } from "@/components/common/types";

type MetaDynamicValues = MetaProperties & { applicationName?: string } & Record<string, unknown>;

type TranslatedMetaByPageId = Record<
  PageId,
  (dynamicValues: MetaDynamicValues) => {
    title: string;
    description: string;
  }
>;

const PAGE_ID_META_2257 = "2257" as const satisfies PageId;

// eslint-disable-next-line id-length, max-lines-per-function -- i18next `t`; one map entry per PageId
function createTranslatedMetaByPageId(t: TFunction): TranslatedMetaByPageId {
  return {
    login: (dynamicValues) => ({
      title: t("meta:login.title", dynamicValues),
      description: t("meta:login.description", dynamicValues),
    }),
    home: (dynamicValues) => ({
      title: t("meta:home.title", dynamicValues),
      description: t("meta:home.description", dynamicValues),
    }),
    chat: (dynamicValues) => ({
      title: t("meta:chat.title", dynamicValues),
      description: t("meta:chat.description", dynamicValues),
    }),
    chats: (dynamicValues) => ({
      title: t("meta:chats.title", dynamicValues),
      description: t("meta:chats.description", dynamicValues),
    }),
    myCharacters: (dynamicValues) => ({
      title: t("meta:myCharacters.title", dynamicValues),
      description: t("meta:myCharacters.description", dynamicValues),
    }),
    drafts: (dynamicValues) => ({
      title: t("meta:drafts.title", dynamicValues),
      description: t("meta:drafts.description", dynamicValues),
    }),
    recommendedBots: (dynamicValues) => ({
      title: t("meta:recommendedBots.title", dynamicValues),
      description: t("meta:recommendedBots.description", dynamicValues),
    }),
    favoriteBots: (dynamicValues) => ({
      title: t("meta:favorites.chatbots.title", dynamicValues),
      description: t("meta:favorites.chatbots.description", dynamicValues),
    }),
    favoritesLorebooks: (dynamicValues) => ({
      title: t("meta:favorites.lorebooks.title", dynamicValues),
      description: t("meta:favorites.lorebooks.description", dynamicValues),
    }),
    favoritesGroups: (dynamicValues) => ({
      title: t("meta:favorites.groups.title", dynamicValues),
      description: t("meta:favorites.groups.description", dynamicValues),
    }),
    blockedCreators: (dynamicValues) => ({
      title: t("meta:blockedCreators.title", dynamicValues),
      description: t("meta:blockedCreators.description", dynamicValues),
    }),
    myPersonas: (dynamicValues) => ({
      title: t("meta:myPersonas.title", dynamicValues),
      description: t("meta:myPersonas.description", dynamicValues),
    }),
    myVoices: (dynamicValues) => ({
      title: t("meta:myVoices.title", dynamicValues),
      description: t("meta:myVoices.description", dynamicValues),
    }),
    createVoice: (dynamicValues) => ({
      title: t(`meta:createVoice.title`, dynamicValues),
      description: t(`meta:createVoice.description`, dynamicValues),
    }),
    createPersona: (dynamicValues) => ({
      title: t("meta:createPersona.title", dynamicValues),
      description: t("meta:createPersona.description", dynamicValues),
    }),
    editPersona: (dynamicValues) => ({
      title: t("meta:editPersona.title", dynamicValues),
      description: t("meta:editPersona.description", dynamicValues),
    }),
    editCharacter: (dynamicValues) => ({
      title: t("meta:editCharacter.title", dynamicValues),
      description: t("meta:editCharacter.description", dynamicValues),
    }),
    createCharacter: (dynamicValues) => ({
      title: t("meta:createCharacter.title", dynamicValues),
      description: t("meta:createCharacter.description", dynamicValues),
    }),
    creatorProfile: (dynamicValues) => ({
      title: t("meta:creatorProfile.title", dynamicValues),
      description: t("meta:creatorProfile.description", dynamicValues),
    }),
    androidInstallation: (dynamicValues) => ({
      title: t("meta:androidInstallation.title", dynamicValues),
      description: t("meta:androidInstallation.description", dynamicValues),
    }),
    signup: (dynamicValues) => ({
      title: t("meta:signup.title", dynamicValues),
      description: t("meta:signup.description", dynamicValues),
    }),
    privacy: (dynamicValues) => ({
      title: t("meta:privacy.title", dynamicValues),
      description: t("meta:privacy.description", dynamicValues),
    }),
    terms: (dynamicValues) => ({
      title: t("meta:terms.title", dynamicValues),
      description: t("meta:terms.description", dynamicValues),
    }),
    complaint: (dynamicValues) => ({
      title: t("meta:complaints.title", dynamicValues),
      description: t("meta:complaints.description", dynamicValues),
    }),
    [PAGE_ID_META_2257]: (dynamicValues) => ({
      title: t("meta:2257.title", dynamicValues),
      description: t("meta:2257.description", dynamicValues),
    }),
    refundPolicy: (dynamicValues) => ({
      title: t("meta:refundPolicy.title", dynamicValues),
      description: t("meta:refundPolicy.description", dynamicValues),
    }),
    notFound: (dynamicValues) => ({
      title: t("meta:notFound.title", dynamicValues),
      description: t("meta:notFound.description", dynamicValues),
    }),
    creatorLeaderboard: (dynamicValues) => ({
      title: t("meta:creatorLeaderboard.title", dynamicValues),
      description: t("meta:creatorLeaderboard.description", dynamicValues),
    }),
    characterDefinition: (dynamicValues) => ({
      title: t("meta:characterDefinition.title", dynamicValues),
      description: t("meta:characterDefinition.description", dynamicValues),
    }),
    report: (dynamicValues) => ({
      title: t("meta:report.title", dynamicValues),
      description: t("meta:report.description", dynamicValues),
    }),
    supportedApplications: (dynamicValues) => ({
      title: t("meta:supportedApplications.title", dynamicValues),
      description: t("meta:supportedApplications.description", dynamicValues),
    }),
    lorebookEdit: (dynamicValues) => ({
      title: t("meta:lorebookEdit.title", dynamicValues),
      description: t("meta:lorebookEdit.description", dynamicValues),
    }),
    lorebookCreate: (dynamicValues) => ({
      title: t("meta:lorebookCreate.title", dynamicValues),
      description: t("meta:lorebookCreate.description", dynamicValues),
    }),
    myLorebooks: (dynamicValues) => ({
      title: t("meta:myLorebooks.title", dynamicValues),
      description: t("meta:myLorebooks.description", dynamicValues),
    }),
    myRulebooks: (dynamicValues) => ({
      title: t("meta:myRulebooks.title", dynamicValues),
      description: t("meta:myRulebooks.description", dynamicValues),
    }),
    myGallery: (dynamicValues) => ({
      title: t("meta:myGallery.title", dynamicValues),
      description: t("meta:myGallery.description", dynamicValues),
    }),
    createGroup: (dynamicValues) => ({
      title: t("meta:createGroup.title", dynamicValues),
      description: t("meta:createGroup.description", dynamicValues),
    }),
    myGroups: (dynamicValues) => ({
      title: t("meta:myGroups.title", dynamicValues),
      description: t("meta:myGroups.description", dynamicValues),
    }),
    subscribePage: (dynamicValues) => ({
      title: t("meta:subscribePage.title", dynamicValues),
      description: t("meta:subscribePage.description", dynamicValues),
    }),
    planComparison: (dynamicValues) => ({
      title: t("meta:planComparison.title", dynamicValues),
      description: t("meta:planComparison.description", dynamicValues),
    }),
    userProfile: (dynamicValues) => ({
      title: t("meta:userProfile.title", dynamicValues),
      description: t("meta:userProfile.description", dynamicValues),
    }),
    chatbotTag: (dynamicValues) => ({
      title: t("meta:chatbotTag.title", dynamicValues),
      description: t("meta:chatbotTag.description", dynamicValues),
    }),
  };
}

export default function useTranslatedMetaInfo() {
  const { t } = useTranslation();

  const translatedMetaByPageId = useMemo(() => createTranslatedMetaByPageId(t), [t]);

  const getTranslatedMetaInfo = useCallback(
    (pageId: PageId, dynamicValues: MetaDynamicValues) => translatedMetaByPageId[pageId](dynamicValues),
    [translatedMetaByPageId],
  );

  return { getTranslatedMetaInfo };
}
